// Angular imports
import {
  Component,
  EventEmitter,
  Input,
  OnInit,
  Output,
  ViewChild,
  AfterViewInit
} from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';

// CC-Common imports
import { Utils } from '@therealmanage/ciranet-ui-common';

// Component imports
import { BaseComponent } from '../../_base/rp-base-component';
import { ChangePasswordComponent } from '../../user-components/change-password/change-password.component';
import { EditProfileComponent } from '../../user-components/edit-profile/edit-profile.component';
import { ImpersonateComponent } from '../../user-components/impersonate/impersonate.component';
import { PreferencesComponent } from '../../user-components/preferences/preferences.component';
import { PortalAnnouncementComponent } from '../portal-announcement/portal-announcement.component';
import { DefaultPropertyComponent } from '../../property-module/default-property/default-property.component';
import { AddPropertyModalComponent } from '../../property-module/add-property-modal/add-property-modal.component';

// Service imports
import { AuthService } from '../../_services/auth.service';
import { UserService } from '../../_services/data-services/user.service';
import { ScreenService } from '../../_services/shared-services/screen.service';
import { HelperService } from '../../_services/shared-services/helper.service';
import { EnvironmentConfigService } from '../../_services/environment-config.service';
import { CustomAnalyticsService } from '../../_services/shared-services/analytics.service';

// Model imports
import { AnnouncementsParametersDto } from '../../_models/announcements-parameters-dto';
import { UserPropertyResultDto } from '../../_models/user-property-result-dto';

// CC-Common imports
import { RxjsUtils } from '@therealmanage/ciranet-ui-common';

@Component({
  selector: 'app-header',
  templateUrl: 'header.component.html',
  styleUrls: ['./header.component.scss'],
  standalone: false
})
export class HeaderComponent
  extends BaseComponent
  implements OnInit, AfterViewInit
{
  @Input() menuToggleEnabled = false;
  @Input() menuUserEnabled = false;
  @Output() menuToggle = new EventEmitter<boolean>();

  @ViewChild('preferenceComponent', { static: false })
  preferenceComponent: PreferencesComponent;
  @ViewChild('changePasswordComponent', { static: false })
  changePasswordComponent: ChangePasswordComponent;
  @ViewChild('editProfileComponent', { static: false })
  editProfileComponent: EditProfileComponent;
  @ViewChild('impersonateComponent', { static: false })
  impersonateComponent: ImpersonateComponent;
  @ViewChild('announcementComponent', { static: false })
  announcementComponent: PortalAnnouncementComponent;
  @ViewChild('defaultProperty', { static: false })
  defaultProperty: DefaultPropertyComponent;
  @ViewChild('addPropertyModal', { static: false })
  addPropertyModal: AddPropertyModalComponent;

  currentEnvironment: string;
  logoLocation: string = 'before';
  userMenuItems = [];
  userProperties: UserPropertyResultDto[];

  returnUrl: string;
  menuButtonWidth: string;
  isSmallScreen: boolean;
  isInternalUser: boolean = false;
  announcementCount: number = 0;
  selectedProperty: UserPropertyResultDto;

  constructor(
    private router: Router,
    private route: ActivatedRoute,
    private authService: AuthService,
    private userService: UserService,
    private helperService: HelperService,
    private environmentConfigService: EnvironmentConfigService,
    screenService: ScreenService,
    private analyticsService: CustomAnalyticsService
  ) {
    super(screenService);

    const currentEnv = this.environmentConfigService.currentApiEnvironment;
    if (Utils.isInList(currentEnv, ['local', 'dev', 'demo', 'qa'])) {
      // Show the environment
      this.currentEnvironment = currentEnv.toUpperCase();
    }
  }

  ngOnInit(): void {
    this.updateScreenSize();
    this.initialize(); // first load

    RxjsUtils.subscribeToQueryParams(this.route, (params) => {
      this.returnUrl = this.route.snapshot.queryParams['returnUrl'];
    });

    RxjsUtils.subscribeToSubject(
      this.authService.getSubjectLoginSuccess(),
      (flag: boolean) => {
        if (flag === true) {
          this.initialize(); // after login
        }
      }
    );

    RxjsUtils.subscribeToSubject(
      this.userService.getSubjectImpersonateChanged(),
      () => {
        this.initialize(); // after impersonate changed
        this.getAnnouncementCount();
      }
    );

    RxjsUtils.subscribeToSubject(
      this.helperService.subjectLaunchPreferences,
      (flag: boolean) => {
        if (flag === true) {
          this.preferenceComponent.show();
        }
      }
    );

    RxjsUtils.safeSubscribe(this.screenService.changed, () =>
      this.updateScreenSize()
    );

    RxjsUtils.subscribeToSubject(
      this.userService.getSubjectPropertySelectionChanged(),
      (data) => {
        if (
          !!this.selectedProperty &&
          this.selectedProperty.communityId != data.communityId
        ) {
          this.getAnnouncementCount();
          this.router.navigate(['/home']);
        }
      }
    );

    RxjsUtils.subscribeToSubject(
      this.userService.getSubjectSelectDefaultProperty(),
      (properties) => {
        this.userProperties = properties;
        this.defaultProperty.show();
      }
    );

    RxjsUtils.subscribeToSubject(
      this.userService.getSubjectShowAddPropertyModal(),
      () => {
        this.addPropertyModal.show();
      }
    );

    this.getAnnouncementCount();
  }

  ngAfterViewInit(): void {
    RxjsUtils.subscribeToSubject(
      this.preferenceComponent.preferrencePopClose,
      () => {
        this.helperService.subjectLaunchPreferences.next(false);
      }
    );
  }

  toggleMenu = () => {
    this.menuToggle.emit();
  };

  onDefaultSelection(selectedProperty: UserPropertyResultDto): void {
    this.userService.updateSubjectPropertySelectionChanged(selectedProperty);
    if (!!this.returnUrl) {
      this.router.navigateByUrl(this.returnUrl);
    }
  }

  onPropertyAdded(): void {
    this.router.navigate(['/home']);
  }

  onAnnouncementClick(): void {
    this.showAnnouncementPopup();
  }

  private initialize(): void {
    this.isInternalUser = this.userService.isInternal();
    this.userMenuItems = [];
    const isImpersonate = this.userService.isImpersonated();
    if (isImpersonate) {
      this.userMenuItems.push({
        text: 'Cancel Impersonation',
        icon: 'fa-solid fa-user-secret',
        onClick: () => {
          this.userService.endImpersonation();
          this.router.navigate(['/home']);
        }
      });
      this.getAnnouncementCount();
    } else if (this.userService.canImpersonate()) {
      this.getAnnouncementCount();
      this.userMenuItems.push({
        text: 'User Impersonation',
        icon: 'fa-solid fa-user-secret',
        onClick: () => {
          this.impersonateComponent.show();
        }
      });
    }

    if (this.isInternalUser === false || isImpersonate) {
      let userId = null;
      const currentUserInfo = this.userService.getCurrentUserInfo();
      if (currentUserInfo && currentUserInfo.userId) {
        userId = currentUserInfo.userId;
      }

      if (userId) {
        this.userMenuItems.push({
          text: 'Edit Profile',
          icon: 'fa-solid fa-user',
          onClick: () => {
            this.trackAccountIconClick('edit_profile');
            this.editProfileComponent.show();
          }
        });

        this.userMenuItems.push({
          text: 'Change Password',
          icon: 'key',
          onClick: () => {
            this.trackAccountIconClick('change_password');
            this.changePasswordComponent.show();
          }
        });
      }
    }

    this.userMenuItems.push({
      text: 'Add Property',
      icon: 'fa-solid fa-plus-square',
      onClick: () => {
        this.trackAccountIconClick('add_property');
        this.router.navigate(['property', 'add-property']);
      }
    });

    this.userMenuItems.push({
      text: 'Preferences',
      icon: 'preferences',
      onClick: () => {
        this.trackAccountIconClick('preferences');
        this.preferenceComponent.show();
      }
    });

    this.userMenuItems.push({
      text: 'Logout',
      icon: 'fa-solid fa-sign-out-alt',
      onClick: () => {
        this.analyticsService.clearUserId();
        this.authService.logOut();
        this.authService.updateSubjectLogout(true);
        this.authService.updateSubjectLoginSuccess(false); //reset flag in the app.component
      }
    });
  }

  private updateScreenSize(): void {
    this.isSmallScreen = this.screenService.isSmallScreen();
    const screenSize = this.screenService.getCurrentScreenSize;
    const screenParams = this.screenService.getScreenParams(screenSize).header;
    this.logoLocation = screenParams.logoLocation;
    this.menuButtonWidth = screenParams.menuButtonWidth;
  }

  private getAnnouncementCount() {
    const param: AnnouncementsParametersDto = new AnnouncementsParametersDto();
    param.clearCache = true;
    RxjsUtils.safeSubscribe(
      this.userService.getAnnouncementCount(param),
      (announcementCount) => {
        this.announcementCount = announcementCount;
      },
      (error) => {}
    );
  }

  private showAnnouncementPopup() {
    // Skip showing announcement popup during OAuth callbacks (e.g., Google account linking)
    if (this.isOAuthCallback()) {
      return;
    }

    if (this.announcementCount > 0) {
      this.announcementComponent.show();
    }
  }

  private isOAuthCallback(): boolean {
    const queryParams = this.route.snapshot.queryParams;
    return !!queryParams['link_google'];
  }

  private trackAccountIconClick(action: string): void {
    this.analyticsService.trackEvent('account_icon_click', {
      action: action
    });
  }
}
